import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { TrendingUp, Download, Loader2, AlertTriangle, Lock } from "lucide-react";
import ProfitTrend from "@/components/ProfitTrend";
import ClientProfitStory from "@/components/ClientProfitStory";

/** Admin-only profit centre: what we bill vs what it costs us, by client. */
export default function ProfitReport() {
  const [openClient, setOpenClient] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - (d.getDate() <= 5 ? 1 : 0));
    return d.toISOString().slice(0, 7);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [y, m] = month.split("-");

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/profit-report?year=${y}&month=${m}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [month]);

  const t = data?.totals;
  const clients = data?.clients || [];

  if (openClient) return <ClientProfitStory accountId={openClient} onBack={() => setOpenClient(null)} />;

  return (
    <div className="space-y-4" data-testid="profit-report">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <TrendingUp className="w-5 h-5 text-emerald-600" /> Profit by client
            </h2>
            <p className="text-sm text-slate-500 mt-0.5 inline-flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> Admin only — what we bill against what the work costs us.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="profit-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
            <a data-testid="profit-export" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/profit-report/export.xlsx?year=${y}&month=${m}`}
              className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm">
              <Download className="w-4 h-4" /> Export
            </a>
          </div>
        </div>

        {t && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4" data-testid="profit-totals">
            <Tile label="Revenue" value={formatCurrency(t.revenue)} />
            <Tile label="Our cost" value={formatCurrency(t.cost)} />
            <Tile label="Profit" value={formatCurrency(t.profit)} accent />
            <Tile label="Margin" value={`${t.margin_pct}%`} />
            <Tile label="Shipping profit" value={formatCurrency(t.shipping_profit)} />
            <Tile label="Avg shipping markup" value={`${t.shipping_markup_pct}%`} />
          </div>
        )}

        {(data?.not_calculated || []).length > 0 && (
          <p className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 inline-flex items-start gap-2" data-testid="profit-missing">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            Not in these numbers yet: {data.not_calculated.join(", ")} — run “Calculate all” on Run the month first.
          </p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="profit-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2.5 px-4">Client</th>
              <th className="py-2.5 px-4 text-right">Orders</th>
              <th className="py-2.5 px-4 text-right">Shipping billed</th>
              <th className="py-2.5 px-4 text-right">Carrier cost</th>
              <th className="py-2.5 px-4 text-right">Shipping profit</th>
              <th className="py-2.5 px-4 text-right">Markup</th>
              <th className="py-2.5 px-4 text-right">Fulfillment profit</th>
              <th className="py-2.5 px-4 text-right">Packaging profit</th>
              <th className="py-2.5 px-4 text-right">Credits</th>
              <th className="py-2.5 px-4 text-right">Profit</th>
              <th className="py-2.5 px-4 text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.account_id} data-testid={`profit-row-${c.account_id}`} onClick={() => setOpenClient(c.account_id)}
                className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                <td className="py-2.5 px-4">
                  <span className="font-semibold text-slate-900 hover:text-blue-700">{c.company_name}</span>
                  {c.costs_source === "account" && <span className="ml-2 text-[10px] uppercase tracking-wide bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">own costs</span>}
                </td>
                <td className="py-2.5 px-4 text-right">{c.orders}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(c.shipping_revenue)}</td>
                <td className="py-2.5 px-4 text-right text-slate-500">{formatCurrency(c.shipping_cost)}</td>
                <td className="py-2.5 px-4 text-right font-semibold text-emerald-700">{formatCurrency(c.shipping_profit)}</td>
                <td className="py-2.5 px-4 text-right">{c.markup_pct_effective == null ? <span className="text-slate-400">n/a</span> : `${c.markup_pct_effective}%`}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(c.fulfillment_profit)}</td>
                <td className="py-2.5 px-4 text-right">{c.packaging_revenue ? formatCurrency(c.packaging_profit) : "—"}</td>
                <td className="py-2.5 px-4 text-right text-orange-700">{c.credits_given ? `− ${formatCurrency(c.credits_given)}` : "—"}</td>
                <td className="py-2.5 px-4 text-right font-bold text-slate-900">{formatCurrency(c.profit)}</td>
                <td className="py-2.5 px-4 text-right">{c.margin_pct}%</td>
              </tr>
            ))}
            {t && clients.length > 0 && (
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                <td className="py-2.5 px-4">All clients</td>
                <td className="py-2.5 px-4 text-right">{t.orders}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.shipping_revenue)}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.shipping_cost)}</td>
                <td className="py-2.5 px-4 text-right text-emerald-700">{formatCurrency(t.shipping_profit)}</td>
                <td className="py-2.5 px-4 text-right">{t.shipping_markup_pct}%</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.fulfillment_profit)}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.packaging_profit)}</td>
                <td className="py-2.5 px-4 text-right text-orange-700">{t.credits_given ? `− ${formatCurrency(t.credits_given)}` : "—"}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.profit)}</td>
                <td className="py-2.5 px-4 text-right">{t.margin_pct}%</td>
              </tr>
            )}
            {clients.length === 0 && (
              <tr><td colSpan={11} className="py-10 text-center text-slate-400">{loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Nothing calculated for this month yet — run “Calculate all” on Run the month."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Carrier cost is the real ShipStation cost. Fulfillment and packaging costs come from “Our costs” (global, or a client’s own). Credits given are subtracted from profit. Click a client for their 12-month story.
      </p>

      <ProfitTrend onOpenClient={setOpenClient} />
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className={`rounded-lg p-3 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}
